import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useIsLandscape } from '../../hooks/useIsLandscape';
import { computeHistogramFromImageData } from '../../lib/adjustments/curves';
import {
  buildAdjustmentConfig,
  type AdjustmentValueByKind,
  type ImplementedAdjustmentKind,
} from '../../lib/adjustments/dispatch';
import type { AdjustmentConfig } from '../../types/image-editor.types';
import {
  ADJUSTMENT_SPECS,
  FILTER_MENU,
  type AdjustmentKind,
  type AdjustmentSpec,
  type AdjustmentPanelProps,
} from './registry';

/** Membership in `FILTER_MENU` is the source of truth for whether a
 *  kind opens as "Filter — …" vs "Adjust — …" in the modal header
 *  and accessibility description. Anchored to the menu (not a string-
 *  prefix convention on the discriminator) so a future filter kind
 *  whose name doesn't start with `filter-` still classifies correctly. */
const FILTER_KIND_SET = new Set<AdjustmentKind>(FILTER_MENU.map((m) => m.kind));
function isFilterKind(kind: AdjustmentKind): boolean {
  return FILTER_KIND_SET.has(kind);
}

interface AdjustmentsModalProps {
  /** Active kind. `null` closes the dialog. The parent owns this so
   *  picker selection (parent-controlled) and Apply/Cancel (which
   *  calls `onClose`) flow through the same state machine. */
  kind: AdjustmentKind | null;
  /** Bumped by the parent on every fresh open. Used as part of the
   *  inner body's `key` so re-opening the same kind back-to-back
   *  forces a re-mount of per-kind state (value, source snapshot,
   *  channel chip, etc.) rather than carrying the previous session
   *  forward. */
  sessionId: number;
  /** Live paint targets — one per layer the adjustment writes into.
   *  Single-element for a non-group active layer; multi-element when
   *  the active layer is a group (one entry per rasterizable
   *  descendant). The body captures `getImageData` from each canvas
   *  itself after first paint so the dialog appears immediately on
   *  huge images instead of blocking the click handler. */
  targetCanvases: HTMLCanvasElement[];
  /** Called after every preview re-paint. Parent uses this to mark
   *  every target's Fabric object dirty + requestRenderAll so the
   *  user actually sees the change. */
  onPreviewTick: () => void;
  /** User clicked Apply. Pixels already on the target canvases;
   *  parent is responsible for the history snapshot + dirty flag. */
  onCommit: () => void;
  /** User clicked Apply with "Apply as adjustment layer" checked.
   *  The body has already reverted the destructive preview pixels
   *  before this fires, so the parent's responsibility is to
   *  create the non-destructive adjustment layer and trigger a
   *  slow-path re-composite. When `undefined`, the toggle is
   *  hidden — used by non-destructive edit mode where the modal
   *  IS an adjustment-layer editor and there's no meaningful
   *  "apply as adjustment layer" option. */
  onCreateAdjustmentLayer?: (config: AdjustmentConfig) => void;
  /** Non-destructive edit mode — opened against an existing
   *  adjustment layer. When set:
   *    - `targetCanvases` is ignored (no destructive preview path
   *      runs; the slow-path compositor handles preview by
   *      re-applying the live config to layers below)
   *    - the body initializes its value from `original`
   *    - every value change fires `onLiveEditValue` so the parent
   *      dispatches `UPDATE_ADJUSTMENT` and the slow-path
   *      compositor re-paints
   *    - the "Apply as adjustment layer" toggle is hidden (no
   *      sense — it IS one)
   *    - Cancel reverts via one final `onLiveEditValue(original)`
   *  Apply just commits the current state (saves a history
   *  snapshot in the parent). */
  editingAdjustment?: { layerId: string; original: AdjustmentConfig };
  /** Live edit callback — fired on every value change in
   *  non-destructive edit mode, and once on Cancel to revert. */
  onLiveEditValue?: (config: AdjustmentConfig) => void;
  /** Dialog should close. Called after Apply, Cancel, or backdrop
   *  dismissal. The body restores the source pixels before calling
   *  this on every non-commit close, so the parent doesn't need to
   *  revert. */
  onClose: () => void;
}

/** Outer shell. Owns the dialog primitive (so radix handles focus
 *  trap + escape + outside-click), the drag-by-header position, and
 *  routes radix's outside-click / Escape close events into the
 *  body's cancel handler so all close paths revert consistently.
 *  Per-kind state lives in `AdjustmentBody`.
 *
 *  Uses radix primitives directly rather than the shared
 *  `DialogContent` wrapper so we can:
 *
 *  1. Skip the dimming `DialogOverlay` — the user wants to preview
 *     true canvas brightness while the dialog is open.
 *  2. Apply the drag offset via `transform` on an inner wrapper
 *     rather than `top`/`left` on the outer Content. `transform` is
 *     GPU-accelerated and skips layout reflow on every pointer-move
 *     event; `top`/`left` would trigger reflow (catastrophic with
 *     the SVG curve editor as a child).
 *
 *  Radix's Content stays centered via its own (animation-friendly)
 *  classes; we never override its `transform`. The inner wrapper
 *  carries the visual chrome (border / bg / shadow / scroll cap)
 *  AND the drag transform, layered cleanly. */
export function AdjustmentsModal({
  kind,
  sessionId,
  targetCanvases,
  onPreviewTick,
  onCommit,
  onCreateAdjustmentLayer,
  editingAdjustment,
  onLiveEditValue,
  onClose,
}: AdjustmentsModalProps) {
  const isLandscape = useIsLandscape();
  const open = kind !== null;

  const spec = useMemo<AdjustmentSpec<unknown> | null>(
    () => (kind ? (ADJUSTMENT_SPECS[kind] ?? null) : null),
    [kind],
  );

  // Hold the last non-null spec across the close transition so radix
  // animates against stable content. The Content's `open` prop drives
  // visibility — `displaySpec` stays populated through the close.
  const [displaySpec, setDisplaySpec] = useState<AdjustmentSpec<unknown> | null>(null);
  useEffect(() => {
    if (spec) setDisplaySpec(spec);
  }, [spec]);

  // Body exposes `cancel()` so the outer dialog's outside-click /
  // Escape / X-button paths route into the same revert flow as the
  // explicit Cancel button. Without this the canvases would freeze
  // at the last-previewed state on those dismissal paths.
  const bodyRef = useRef<{ cancel: () => void } | null>(null);

  // Drag-by-header. State lives here (not in the body) so it
  // survives re-mounts within a session. Reset on each fresh open
  // via `sessionId` so the dialog comes back centered every time.
  const drag = useDraggable();
  const resetDrag = drag.reset;
  useEffect(() => {
    resetDrag();
  }, [sessionId, resetDrag]);

  // Portrait / mobile: dock as a bottom sheet instead of a draggable
  // centered dialog. On touch the drag-by-header gesture is awkward and
  // the dialog floats over the canvas with nothing capturing background
  // taps — so taps fell through to the canvas and the page/canvas
  // scrolled behind it. The sheet docks to the bottom (no repositioning
  // needed), and a transparent (non-dimming) overlay below it captures
  // background pointer/scroll while keeping the visible canvas above the
  // sheet at true brightness for live preview. Landscape / desktop keeps
  // the draggable dialog — there the user moves it to peek behind it.
  const isSheet = !isLandscape;

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          if (bodyRef.current) bodyRef.current.cancel();
          else onClose();
        }
      }}
    >
      {displaySpec && (
        <DialogPrimitive.Portal>
          {/* No DIMMING overlay — the canvas stays at true brightness so
              live preview is accurate. In sheet mode we still render a
              fully TRANSPARENT overlay so background taps/scroll/pan are
              captured (radix's modal scroll-lock doesn't stop the canvas's
              own touch handlers) instead of leaking through to the canvas.
              The draggable-dialog mode keeps no overlay so the user can
              still pan/zoom the canvas while repositioning the dialog. */}
          {isSheet && <div className="fixed inset-0 z-40" aria-hidden />}
          <DialogPrimitive.Content
            className={
              isSheet
                ? // Bottom sheet: docked full-width, slides up from the
                  // bottom. No drag offset.
                  'fixed inset-x-0 bottom-0 z-50 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom'
                : // Centering + open/close animations live on the radix
                  // Content. We never override its `transform` so the
                  // animation-fill cleanly hands off to its own computed
                  // style after the open animation. The visual chrome and
                  // drag offset live on the inner div below.
                  'fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
            }
          >
            <div
              className={
                isSheet
                  ? 'relative bg-background border-t shadow-lg rounded-t-xl overflow-y-auto max-h-[85vh] w-full'
                  : 'relative bg-background border shadow-lg sm:rounded-lg overflow-y-auto max-h-[90vh] w-[calc(100vw-2rem)] max-w-md'
              }
              style={
                isSheet
                  ? undefined
                  : {
                      // GPU-accelerated drag offset. `translate3d` hints
                      // the compositor; no layout reflow per pointer-move.
                      transform: `translate3d(${drag.pos.x}px, ${drag.pos.y}px, 0)`,
                    }
              }
            >
              <div
                {...(isSheet ? {} : drag.handlers)}
                className={`sticky top-0 z-10 bg-background px-6 pt-6 pb-3 select-none flex flex-col space-y-1.5 text-center sm:text-left ${
                  isSheet ? '' : drag.dragging ? 'cursor-grabbing' : 'cursor-grab'
                }`}
                title={isSheet ? undefined : 'Drag to reposition'}
              >
                {/* Grabber affordance — purely visual, signals the docked
                    sheet. */}
                {isSheet && (
                  <div className="mx-auto -mt-3 mb-1 h-1 w-10 rounded-full bg-border" aria-hidden />
                )}
                <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                  {isFilterKind(displaySpec.kind) ? 'Filter' : 'Adjust'} — {displaySpec.title}
                  {targetCanvases.length > 1 && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      ({targetCanvases.length} layers)
                    </span>
                  )}
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="sr-only">
                  {displaySpec.title} {isFilterKind(displaySpec.kind) ? 'filter' : 'adjustment'}{' '}
                  dialog. {isSheet ? '' : 'Drag the header to reposition; '}drag panel controls to
                  preview; Apply commits the change to{' '}
                  {targetCanvases.length > 1
                    ? `all ${targetCanvases.length} rasterizable layers in the active group`
                    : 'the active layer'}
                  .
                </DialogPrimitive.Description>
                <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
              </div>
              <div className="px-6 pb-6 pt-1 space-y-4">
                <AdjustmentBody
                  key={`${displaySpec.kind}-${sessionId}`}
                  ref={bodyRef}
                  spec={displaySpec}
                  targetCanvases={targetCanvases}
                  isOpen={open}
                  onPreviewTick={onPreviewTick}
                  onCommit={onCommit}
                  onCreateAdjustmentLayer={onCreateAdjustmentLayer}
                  editingAdjustment={editingAdjustment}
                  onLiveEditValue={onLiveEditValue}
                  onClose={onClose}
                />
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      )}
    </DialogPrimitive.Root>
  );
}

interface AdjustmentBodyProps {
  spec: AdjustmentSpec<unknown>;
  targetCanvases: HTMLCanvasElement[];
  isOpen: boolean;
  onPreviewTick: () => void;
  onCommit: () => void;
  onCreateAdjustmentLayer?: (config: AdjustmentConfig) => void;
  editingAdjustment?: { layerId: string; original: AdjustmentConfig };
  onLiveEditValue?: (config: AdjustmentConfig) => void;
  onClose: () => void;
  ref?: React.Ref<{ cancel: () => void }>;
}

/** One per active target. Bundles the source snapshot + the
 *  downscaled-preview pipeline so the per-frame work in the rAF
 *  loop is a simple foreach over an array of these. */
interface PreviewPipeline {
  /** Live target canvas the spec writes into and Cancel reverts. */
  target: HTMLCanvasElement;
  /** Full-resolution source pixels — captured once at open. Used by
   *  Apply (final full-res pass) and Cancel (blit to revert). */
  source: ImageData;
  /** Source for the live-preview path. Equal to `source` when the
   *  layer is small enough; downscaled to MAX_PREVIEW_DIM otherwise. */
  previewSource: ImageData;
  /** Offscreen target the spec writes into during preview when the
   *  downscale path is active. `null` when the layer fits in full at
   *  preview-time and the spec writes directly into `target`. */
  previewOffscreen: HTMLCanvasElement | null;
}

const MAX_PREVIEW_DIM = 768;

/** Strip the discriminator `kind` field from an AdjustmentConfig
 *  so the remainder matches the kind-specific Value type the
 *  panels accept. */
function stripKind(config: AdjustmentConfig): unknown {
  const { kind: _kind, ...rest } = config;
  return rest;
}

function AdjustmentBody({
  spec,
  targetCanvases,
  isOpen,
  onPreviewTick,
  onCommit,
  onCreateAdjustmentLayer,
  editingAdjustment,
  onLiveEditValue,
  onClose,
  ref,
}: AdjustmentBodyProps) {
  // "Apply as adjustment layer" toggle. Local to the body — bumped
  // each session (the body re-mounts on session bump), so the
  // toggle resets between separate Adjust… clicks. Defaults off
  // (destructive Apply is the legacy behavior). Hidden entirely
  // when the parent doesn't pass an `onCreateAdjustmentLayer` or
  // when an `editingAdjustment` is in play — the layer IS already
  // an adjustment, so the toggle is meaningless.
  const [applyAsLayer, setApplyAsLayer] = useState(false);
  const isEditMode = editingAdjustment != null;
  // Initializer runs once at mount with the correct spec — no race
  // against the live-preview effect since both observe `value`
  // populated by render-time. In edit mode the starting value
  // comes from the layer's current config (stripped of its
  // discriminator `kind` field, which the body re-stamps when
  // calling back into `onLiveEditValue`).
  const [value, setValue] = useState<unknown>(() => {
    if (editingAdjustment) return stripKind(editingAdjustment.original);
    return spec.identity();
  });
  const [pipelines, setPipelines] = useState<PreviewPipeline[] | null>(null);

  // Build per-target preview pipelines after first paint. Source
  // capture (`getImageData` per layer, plus the downscale render pass
  // for layers >MAX_PREVIEW_DIM) blocks the main thread for the
  // duration; deferring to a post-mount effect lets the dialog
  // appear immediately rather than freezing behind the work.
  //
  // Invariant: `targetCanvases` is fixed for the body's lifetime.
  // The parent always changes targets + sessionId together (which
  // remounts the body), so the body never sees its targets swap
  // mid-session. Dev warning catches future regressions.
  const capturedTargetsRef = useRef<HTMLCanvasElement[] | null>(null);
  useEffect(() => {
    // Edit-mode bypasses the entire destructive preview pipeline:
    // the slow-path compositor in `render-pipeline.ts` re-applies
    // the adjustment whenever `onLiveEditValue` triggers an
    // `UPDATE_ADJUSTMENT` dispatch, so there's no per-target
    // source to capture.
    if (isEditMode) return;
    if (targetCanvases.length === 0) return;
    // Short-circuit if the array contents are identity-equal to what
    // we already captured. The parent's `targetCanvases` prop is now
    // memoized, so this should rarely trigger — but the body lives
    // through the entire dialog session and React may re-fire effects
    // for reasons unrelated to the prop value (StrictMode double-
    // invoke, sibling state changes the parent passes through). The
    // capture path is expensive (full-resolution `getImageData` per
    // target plus a 4K→preview-size canvas downscale) so a stable
    // identity check is worth it as defense in depth.
    const prev = capturedTargetsRef.current;
    if (
      prev &&
      prev.length === targetCanvases.length &&
      prev.every((c, i) => c === targetCanvases[i])
    ) {
      return;
    }
    if (process.env.NODE_ENV !== 'production' && prev !== null) {
      console.error(
        '[AdjustmentBody] targetCanvases changed without a session bump — race with downscale-preview pipeline. Parent should remount the body via sessionId.',
      );
    }
    capturedTargetsRef.current = targetCanvases;

    const built: PreviewPipeline[] = [];
    // Specs can opt out of the downscale path when their effect
    // depends on per-pixel character that can't be approximated by
    // scaling parameters (Median's integer radius, Add Noise's
    // per-pixel hash). These filters preview at full source
    // resolution; the tradeoff is potential slider-drag lag, which
    // is worth it to keep preview visually honest about the eventual
    // Apply.
    const fullResRequired = spec?.previewAtFullResolution === true;
    for (const target of targetCanvases) {
      // Layer canvases are created with `willReadFrequently: true`
      // upstream (ensureCanvasElement, ensurePaintSurface). Pass the
      // same flag here as defense-in-depth — if a future path
      // forgets the upstream init, this getContext call still hits
      // a CPU-readable context. The flag is set on first getContext;
      // we'd be downgrading nothing.
      const ctx = target.getContext('2d', { willReadFrequently: true });
      if (!ctx) continue;
      const source = ctx.getImageData(0, 0, target.width, target.height);
      const longEdge = Math.max(source.width, source.height);
      if (fullResRequired || longEdge <= MAX_PREVIEW_DIM) {
        built.push({ target, source, previewSource: source, previewOffscreen: null });
        continue;
      }
      // Downscale path — see comments in the singleton implementation
      // before refactor: same logic, just per-target.
      const scale = MAX_PREVIEW_DIM / longEdge;
      const pw = Math.max(1, Math.round(source.width * scale));
      const ph = Math.max(1, Math.round(source.height * scale));
      const tmp = document.createElement('canvas');
      tmp.width = source.width;
      tmp.height = source.height;
      const tmpCtx = tmp.getContext('2d', { willReadFrequently: true });
      if (!tmpCtx) continue;
      tmpCtx.putImageData(source, 0, 0);
      const small = document.createElement('canvas');
      small.width = pw;
      small.height = ph;
      const smallCtx = small.getContext('2d', { willReadFrequently: true });
      if (!smallCtx) continue;
      smallCtx.imageSmoothingQuality = 'high';
      smallCtx.drawImage(tmp, 0, 0, pw, ph);
      built.push({
        target,
        source,
        previewSource: smallCtx.getImageData(0, 0, pw, ph),
        previewOffscreen: small,
      });
    }
    setPipelines(built);
  }, [targetCanvases, isEditMode, spec]);

  // Histogram from the FIRST target's captured source. Multi-target
  // sessions show histogram for the front-most rasterizable layer
  // only — combining across all sources would scale linearly with
  // group size and offer marginal user benefit (the panels apply
  // the same value to every target either way).
  const histogram = useMemo(
    () =>
      pipelines && pipelines.length > 0
        ? computeHistogramFromImageData(pipelines[0].source)
        : undefined,
    [pipelines],
  );

  // Source dimensions from the front-most pipeline. Edit mode never
  // captures a pipeline, so this is `undefined` there — panels with
  // spatial UI fall back to a default aspect ratio in that case.
  const sourceDimensions = useMemo(
    () =>
      pipelines && pipelines.length > 0
        ? { width: pipelines[0].source.width, height: pipelines[0].source.height }
        : undefined,
    [pipelines],
  );

  // Live preview pump. Skips the first render for adjustments whose
  // identity is a source pass-through (Hue/Sat at 0/0/0, B-C at 0/0,
  // linear Curve, etc.) — the canvases already show source pixels so
  // re-applying identity would be a wasted pixel scan. The ref flips
  // on the user's first edit and stays on for the rest of the
  // session, so Reset (which sends value back to identity) still
  // triggers the repaint it needs.
  //
  // Adjustments whose identity IS a transformation (Black & White's
  // default mix, future Gradient Map / Channel Mixer / etc.) opt
  // into running on mount via `spec.previewIdentityOnMount` — without
  // it the user would open the dialog and see the source unchanged
  // until they touched a slider.
  //
  // Throttled to one re-apply per animation frame. The frame's
  // work loops over every pipeline, applying the spec to each
  // target's preview path. We always apply the LATEST pending
  // value when the rAF fires; intermediate values are coalesced.
  const userInteractedRef = useRef(false);
  const pipelinesRef = useRef<PreviewPipeline[] | null>(null);
  const pendingValueRef = useRef<unknown>(null);
  const rafRef = useRef<number | null>(null);
  pipelinesRef.current = pipelines;

  useEffect(() => {
    // Edit mode previews through the slow-path compositor — the
    // destructive rAF pump is replaced with a dedicated effect
    // (below) that hands the live value to the parent via
    // `onLiveEditValue`.
    if (isEditMode) return;
    if (!isOpen || !pipelines || pipelines.length === 0) return;
    if (!userInteractedRef.current && !spec.previewIdentityOnMount) return;
    pendingValueRef.current = value;
    if (rafRef.current !== null) return; // re-apply already scheduled
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const pl = pipelinesRef.current;
      const v = pendingValueRef.current;
      if (!pl || v === null) return;
      for (const p of pl) {
        if (p.previewOffscreen) {
          // Downscaled preview path: apply at small size, then
          // stretch the result onto the visible target.
          //
          // Spatial filters whose pixel-radius parameter is
          // resolution-dependent (Gaussian / Box / Motion / Unsharp
          // / Smart Sharpen / Reduce Noise) supply `scaleForPreview`
          // to scale `radius` (or `distance`) by the downscale
          // ratio — without this, the preview would look more
          // aggressive than the eventual Apply because a fixed
          // pixel-radius covers a larger fraction of the smaller
          // preview source. The cast preserves the same `unknown`
          // value type the rest of this loop operates on; the spec
          // is typed against its own generic at definition site.
          const ratio = p.previewSource.width / p.source.width;
          const previewValue = spec.scaleForPreview
            ? (spec.scaleForPreview as (val: unknown, r: number) => unknown)(v, ratio)
            : v;
          spec.applyToCanvas(p.previewSource, p.previewOffscreen, previewValue);
          const tCtx = p.target.getContext('2d');
          if (tCtx) {
            tCtx.imageSmoothingEnabled = true;
            tCtx.imageSmoothingQuality = 'low';
            tCtx.drawImage(p.previewOffscreen, 0, 0, p.target.width, p.target.height);
          }
        } else {
          // Full-res path: either the source was small enough to
          // skip downscaling, or the spec opted into full-res via
          // `previewAtFullResolution`. Pass `value` through
          // unchanged — no scaling needed when preview === apply
          // dimensions.
          spec.applyToCanvas(p.previewSource, p.target, v);
        }
      }
      onPreviewTick();
    });
  }, [isEditMode, isOpen, spec, pipelines, value, onPreviewTick]);

  // Edit-mode live preview: every value change dispatches an
  // `UPDATE_ADJUSTMENT` via the parent, the document changes, the
  // slow-path effect re-runs the compositor, and the user sees the
  // updated pixels. The dispatch path is intrinsically batched by
  // React + the slow-path effect, so we don't need an additional
  // rAF throttle here. Skipped on the first render (value still
  // equals the layer's original config — nothing to re-apply).
  const editModeFirstRenderRef = useRef(true);
  useEffect(() => {
    if (!isEditMode || !onLiveEditValue) return;
    if (editModeFirstRenderRef.current) {
      editModeFirstRenderRef.current = false;
      return;
    }
    const config = buildAdjustmentConfig(
      spec.kind as ImplementedAdjustmentKind,
      value as AdjustmentValueByKind[ImplementedAdjustmentKind],
    );
    onLiveEditValue(config);
  }, [isEditMode, onLiveEditValue, spec, value]);

  // Cancel any in-flight rAF on unmount so a stale re-apply can't
  // fire after the body is gone (e.g., user closes mid-drag).
  useEffect(
    () => () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    },
    [],
  );

  // Blit each pipeline's cached source back onto its target — the
  // cheap revert path. Used by Cancel, Escape, outside-click, and
  // X-button. Much faster than calling `spec.applyToCanvas(source,
  // target, identity)` per target (which would still iterate every
  // pixel through a no-op LUT).
  const revertToSources = useCallback(() => {
    const pl = pipelinesRef.current;
    if (!pl) return;
    for (const p of pl) {
      const ctx = p.target.getContext('2d');
      if (!ctx) continue;
      ctx.putImageData(p.source, 0, 0);
    }
    onPreviewTick();
  }, [onPreviewTick]);

  const handleChange = useCallback((next: unknown) => {
    userInteractedRef.current = true;
    setValue(next);
  }, []);

  const handleReset = useCallback(() => {
    userInteractedRef.current = true;
    setValue(spec.identity());
  }, [spec]);

  const handleApply = useCallback(() => {
    if (isEditMode) {
      // No destructive pixels to commit — the slow-path
      // compositor has been painting the live config the whole
      // time. Snapshot-and-close.
      onCommit();
      onClose();
      return;
    }
    if (applyAsLayer && onCreateAdjustmentLayer) {
      // Non-destructive path: revert the destructive preview
      // first, then hand the config off to the parent. The parent
      // dispatches ADD_ADJUSTMENT + creates the Fabric backing
      // object; the slow-path compositor re-applies the same
      // value on its first tick, so the user sees no visual flash
      // between the revert and the new adjustment landing.
      if (userInteractedRef.current) revertToSources();
      const config = buildAdjustmentConfig(
        spec.kind as ImplementedAdjustmentKind,
        value as AdjustmentValueByKind[ImplementedAdjustmentKind],
      );
      onCreateAdjustmentLayer(config);
      onClose();
      return;
    }
    // Final full-res pass when any preview was downscaled — the
    // visible targets currently show the upscaled small results, so
    // committing without this would bake the blur into history.
    // Per-target check: only re-apply targets whose preview path
    // was downscaled (`previewOffscreen` non-null).
    //
    // The gate accepts EITHER the user moved a slider OR the spec
    // opts into `previewIdentityOnMount`. The second case is for
    // adjustments whose identity is a real transformation (Black &
    // White): the user can open the dialog, see the default mix in
    // preview, and click Apply without touching a slider. Without
    // this branch the visible target would still be the upscaled
    // small preview and Apply would commit blurry pixels, or — for
    // a small image where no downscale happened — the rAF pump's
    // direct full-res write might not have fired yet (race between
    // mount-effect rAF and the user's click).
    const pl = pipelinesRef.current;
    const shouldCommitFullRes = userInteractedRef.current || spec.previewIdentityOnMount === true;
    if (shouldCommitFullRes && pl) {
      let anyRepaint = false;
      for (const p of pl) {
        // `previewIdentityOnMount` covers the case where the rAF
        // pump may not have fired yet at all (race with Apply
        // click), so for that path we apply to every pipeline —
        // both downscaled targets (the usual full-res commit) and
        // direct-full-res targets where we just want to ensure
        // pixels land. For interactive Apply, the existing
        // downscale-only filter is sufficient because direct full-
        // res targets are already at the latest pump value.
        const needsApply = spec.previewIdentityOnMount === true || p.previewOffscreen !== null;
        if (!needsApply) continue;
        spec.applyToCanvas(p.source, p.target, value);
        anyRepaint = true;
      }
      if (anyRepaint) onPreviewTick();
    }
    onCommit();
    onClose();
  }, [
    isEditMode,
    applyAsLayer,
    onCreateAdjustmentLayer,
    spec,
    value,
    onCommit,
    onClose,
    onPreviewTick,
    revertToSources,
  ]);

  const handleCancel = useCallback(() => {
    if (isEditMode) {
      // Restore the layer's original config via one final
      // `onLiveEditValue`. The slow-path compositor re-runs and
      // returns the canvas to its pre-edit visual state.
      if (userInteractedRef.current && editingAdjustment && onLiveEditValue) {
        onLiveEditValue(editingAdjustment.original);
      }
      onClose();
      return;
    }
    if (userInteractedRef.current) revertToSources();
    onClose();
  }, [isEditMode, editingAdjustment, onLiveEditValue, revertToSources, onClose]);

  // Expose cancel to the outer shell so escape / outside-click /
  // X-button all route through the same revert path.
  useImperativeHandle(ref, () => ({ cancel: handleCancel }), [handleCancel]);

  const Panel = spec.Panel as React.ComponentType<AdjustmentPanelProps<unknown>>;
  // In edit mode the body is ready immediately — there's no
  // source-capture phase to wait on. In destructive mode the
  // pipeline must be built before the Panel can render meaningful
  // controls.
  const ready = isEditMode || (pipelines !== null && pipelines.length > 0);

  return (
    <>
      {ready ? (
        <Panel
          value={value}
          onChange={handleChange}
          histogram={histogram}
          sourceDimensions={sourceDimensions}
        />
      ) : (
        <div className="py-6 text-center text-xs text-muted-foreground">Preparing preview…</div>
      )}
      {onCreateAdjustmentLayer && !isEditMode && (
        <label className="flex items-center gap-2 pt-2 text-xs text-muted-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 cursor-pointer accent-primary"
            checked={applyAsLayer}
            onChange={(e) => setApplyAsLayer(e.target.checked)}
            disabled={!ready}
          />
          {isFilterKind(spec.kind) ? 'Apply as filter layer' : 'Apply as adjustment layer'}
        </label>
      )}
      <div className="flex flex-row justify-between gap-2 pt-2">
        <button
          type="button"
          disabled={!ready}
          className="px-4 py-2 text-sm rounded-md border border-border text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleReset}
        >
          Reset
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            className="px-4 py-2 text-sm rounded-md border border-border text-muted-foreground hover:bg-accent"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!ready}
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
}

/** Drag-by-handle hook. Returns the cumulative offset, dragging
 *  flag, pointer event handlers (spread onto the drag handle), and
 *  a `reset()` to recenter. The handle owns the pointer capture so
 *  the drag survives if the cursor leaves the handle (e.g., the
 *  user is moving fast). */
function useDraggable() {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const stateRef = useRef<{
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
    pointerId: number;
    handle: HTMLElement;
  } | null>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      // Don't start drag from interactive children inside the handle.
      const target = e.target as HTMLElement;
      if (target.closest('button, input, select, textarea, a, [role="slider"]')) return;
      stateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        baseX: pos.x,
        baseY: pos.y,
        pointerId: e.pointerId,
        handle: e.currentTarget,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
    },
    [pos.x, pos.y],
  );

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const s = stateRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    setPos({
      x: s.baseX + (e.clientX - s.startX),
      y: s.baseY + (e.clientY - s.startY),
    });
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const s = stateRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    s.handle.releasePointerCapture(e.pointerId);
    stateRef.current = null;
    setDragging(false);
  }, []);

  // Bail out via `prev` when already at the origin — a fresh
  // `{x:0, y:0}` object literal would otherwise count as a state
  // change to React, commit a re-render, and (combined with `drag`
  // being in the parent's effect deps) loop forever.
  const reset = useCallback(
    () => setPos((prev) => (prev.x === 0 && prev.y === 0 ? prev : { x: 0, y: 0 })),
    [],
  );

  return useMemo(
    () => ({
      pos,
      dragging,
      reset,
      handlers: {
        onPointerDown: handlePointerDown,
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerUp,
        onPointerCancel: handlePointerUp,
      },
    }),
    [pos, dragging, reset, handlePointerDown, handlePointerMove, handlePointerUp],
  );
}
